import React, { useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { Box, Grid, Typography } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import { BasicTable, MarkComplete } from '../../components';
import {
	DEFAULT_COLUMNS,
	ERROR_MESSAGE,
	PRINTER_ERROR,
	PRINTER_LOADING,
	OPTIONAL_COLUMNS,
	OPERATIONS,
	CHASIS_LINE_IDS,
	COLUMNS_TO_HIDE_FOR_CHASSIS_LINE,
} from '../../constants';
import {
	updateCurrentLineItem,
	updateLastLineItem,
} from '../../redux/actionCreators/station';
import {
	markComplete,
	printBarcode,
} from '../../redux/actionCreators/printBarcode';
import { useMessageContextManager } from '../../components/MessageOverlay/MessageOverlay';
import { useOutletContext } from 'react-router-dom';
import { updateTab } from '../../redux/actionCreators/tab';
import { useRedirectToNextScreen } from '../../hooks';
import JlrButton from '../../components/JlrButton/JlrButton';

const folder_name = 'assembly-error-log';
const file_name = 'printbarcode.txt';

const mapStateToProps = (state) => {
	return {
		currentLineItem: state.station.currentLineItem,
		workStreamType: state.user.workStreamType,
		user: state.user,
		themeMode: state.theme.themeMode,
	};
};

const mapDispatchToProps = (dispatch) => ({
	updateLastLineItem: (lastLineItem) =>
		dispatch(updateLastLineItem(lastLineItem)),
	updateCurrentLineItem: (lastLineItem) =>
		dispatch(updateCurrentLineItem(lastLineItem)),
	printBarcode: (successCallback, errorCallback, sequenceNumber) =>
		dispatch(printBarcode(successCallback, errorCallback, sequenceNumber)),
	markComplete: (successCallback, errorCallback, sequenceNumber) =>
		dispatch(markComplete(successCallback, errorCallback, sequenceNumber)),
	updateTab: (data) => dispatch(updateTab(data)),
});

const classNames = {
	paper: {
		height: '100%',
		display: 'flex',
		flexDirection: 'column',
	},
	tableContentContainer: {
		flex: 1,
		backgroundColor: 'background.default',
		borderBottomLeftRadius: '5px',
		borderBottomRightRadius: '5px',
		padding: '20px',
	},
};

const OrderSuccess = ({ serialNumber }) => {
	return (
		<>
			<Typography variant="h3" sx={{ color: '#82C1A7' }}>
				Order successfully started for the
			</Typography>
			<Typography variant="h3" sx={{ color: '#82C1A7' }}>
				Serial No. {serialNumber}
			</Typography>
		</>
	);
};

export const PrintBarcode = ({
	currentLineItem,
	printBarcode,
	markComplete,
	updateLastLineItem,
	updateCurrentLineItem,
	updateTab,
	user,
	themeMode,
}) => {
	const [currentAgsnData, setCurrentAgsnData] = useState({});
	const { showMessageOverlay } = useMessageContextManager();
	const redirect = useRedirectToNextScreen();
	const { handleNext } = useOutletContext();
	const [printBarcodeStatus, setprintBarcodeStatus] = useState(false);

	const renderCustomError = () => {
		return (
			<>
				<div>1. Check the connection</div>
				<div>2. Check the network </div>
				<div>
					3. If issue persists, contact the local IPMS 4.0 IT support team
				</div>
			</>
		);
	};

	const ctaPrintBarode = () => {
		const closeMesssageOverlay = showMessageOverlay({
			contextText: PRINTER_LOADING.SERIAL_NUMBER_ERROR_CONTEXT_TEXT,
			loading: true,
		});

		printBarcode(
			(data) => {
				// info('ZPL string received successfully');
				const zplstring = encodeURIComponent(data.value);
				window.location.href = `nirmaan://?cmd=print&zplstring=${zplstring}`;
				setTimeout(() => {
					closeMesssageOverlay();
				}, 3000);
				setprintBarcodeStatus(true);
				updateCurrentLineItem({
					...currentAgsnData,
					completedOperations: [
						...new Set([
							...(currentLineItem.completedOperations || []),
							OPERATIONS.PRINT_LABEL,
						]),
					],
				});
			},
			(error) => {
				closeMesssageOverlay();
				showMessageOverlay({
					contextText: PRINTER_ERROR.ERROR_CONTEXT_TEXT + 'Print Barcode',
					info: error?.response?.data?.message,
					error: true,
				});
				const folderName = encodeURIComponent(folder_name);
				const fileName = encodeURIComponent(file_name);
				const printBarcodeError = `Print Barcode Error :- ${JSON.stringify(
					error?.response?.data,
				)}`;
				window.location.href = `nirmaan://run?cmd=create_file&folder=${folderName}&file=${fileName}&content=${printBarcodeError}`;
			},
			currentLineItem.sequenceNumber,
		);
	};

	const ctaMarkComplete = () => {
		markComplete(
			() => {
				const closeOverlay = showMessageOverlay({
					contextText: ' ',
					info: ' ',
					success: true,
					render: () => (
						<OrderSuccess serialNumber={currentLineItem.serialNumber} />
					),
				});
				updateLastLineItem({ ...currentAgsnData });
				updateTab(0);
				setTimeout(() => {
					closeOverlay();
					redirect(() => handleNext());
				}, 3000);
			},
			(error) => {
				showMessageOverlay({
					contextText: ERROR_MESSAGE.MARK_COMPLETE_ERROR_MESSAGE,
					info: error?.response?.data?.message,
					error: true,
				});
				// logError(`Mark complete ${error}`);
			},
			currentLineItem.sequenceNumber,
		);
	};

	const filteredOriginalDefaultColumns = useMemo(() => {
		if (CHASIS_LINE_IDS.includes(user?.lineId)) {
			return DEFAULT_COLUMNS.filter((res) => {
				return !COLUMNS_TO_HIDE_FOR_CHASSIS_LINE.includes(res.field);
			});
		}
		return DEFAULT_COLUMNS; // Return the array directly
	}, [DEFAULT_COLUMNS, user?.lineId]);

	useEffect(() => {
		if (Object.keys(currentLineItem).length > 0) {
			setCurrentAgsnData({
				...currentLineItem,
				id: currentLineItem.sequenceNumber,
				readyForDropSequenceNumber: currentLineItem.metaData.readyForDropSequenceNumber,
				vcDescription: currentLineItem.metaData.vcDescription,
				vcSequenceNumber: currentLineItem.metaData.vcSequenceNumber,
				vcPartNumber: currentLineItem.metaData.vcPartNumber,
				shiftLabel: currentLineItem.metaData.shiftLabel,
				vcNumber: currentLineItem.metaData.vcNumber,
			});
		}
	}, [currentLineItem]);

	return (
		<>
			<BasicTable
				rows={[currentAgsnData]}
				columns={[...filteredOriginalDefaultColumns, ...OPTIONAL_COLUMNS]}
				defaultColumns={[...filteredOriginalDefaultColumns]}
				subtitle="Current ASN/AgSN"
				tableStyles={{ paper: classNames.paper }}
			>
				<Box sx={classNames.tableContentContainer}>
					<Grid container direction="column" height="100%">
						<Grid item container flexGrow={1}>
							<Grid item>
								<Typography
									variant="body3"
									sx={{ color: (theme) => theme.palette.text.secondary }}
								>
									Aggregate Serial No.
								</Typography>
								<Grid container direction="row" alignItems="center" spacing={3}>
									<Grid item>
										<Typography variant="h5" color="primary">
											{currentLineItem.serialNumber}
										</Typography>
									</Grid>
								</Grid>
							</Grid>
						</Grid>
						<Grid item container alignItems="center" columnSpacing={2}>
							<Grid item xs={6}>
								<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
									<CheckIcon
										color="primary"
										sx={{
											fontSize: {
												lg: 34,
											},
										}}
									/>
									<Typography variant="h5" color="primary" fontWeight={400}>
										{!printBarcodeStatus
											? 'Serial number generated.'
											: 'Barcode successfully printed for the above Serial No.'}
									</Typography>
								</Box>
							</Grid>

							<Grid item xs={6} textAlign="right">
								{!printBarcodeStatus ? (
									<JlrButton
										variant="success"
										size="large"
										onClick={ctaPrintBarode}
									>
										Print Barcode
									</JlrButton>
								) : (
									<Grid
										container
										columnSpacing={2}
										rowSpacing={2}
										justifyContent="right"
									>
										<Grid item>
											<JlrButton
												variant="warning"
												size="medium"
												onClick={ctaPrintBarode}
											>
												Reprint Barcode
											</JlrButton>
										</Grid>
										<Grid item>
											<MarkComplete handleButtonClick={ctaMarkComplete} />
										</Grid>
									</Grid>
								)}
							</Grid>
						</Grid>
					</Grid>
				</Box>
			</BasicTable>
		</>
	);
};

export default connect(mapStateToProps, mapDispatchToProps)(PrintBarcode);
